import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  InputAdornment
} from '@mui/material'
import { MergeType as MergeIcon, Search as SearchIcon } from '@mui/icons-material'
import { ticketService } from '@shared/services/TicketService'
import { Ticket } from '@shared/types/entities'

interface MergeTicketDialogProps {
  open: boolean
  onClose: () => void
  sourceTicketId: string
  sourceTicketNumber: string
  // Called after a successful merge with the surviving (target) ticket id, so the
  // caller can navigate there (the source ticket is now closed/merged).
  onMergeSuccess?: (targetTicketId: string) => void
}

export function MergeTicketDialog({
  open,
  onClose,
  sourceTicketId,
  sourceTicketNumber,
  onMergeSuccess
}: MergeTicketDialogProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<Ticket[]>([])
  const [searching, setSearching] = useState(false)
  const [target, setTarget] = useState<Ticket | null>(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  // Debounced search for the surviving ticket. Excludes the source ticket itself.
  useEffect(() => {
    if (target) return // already chosen
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const term = searchTerm.trim()
    if (term.length < 2) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true)
        const res = await ticketService.getAllTickets(1, 10, { search: term })
        setResults(res.data.filter(t => t.ticketId !== sourceTicketId))
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchTerm, target, sourceTicketId])

  const handleSubmit = async () => {
    if (!target || !reason.trim()) return
    try {
      setLoading(true)
      setError(null)
      const res = await ticketService.mergeTickets(target.ticketId, [sourceTicketId], reason.trim())
      setSuccess(true)
      setTimeout(() => {
        if (onMergeSuccess) onMergeSuccess(res.targetTicketId)
        handleClose()
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Failed to merge ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    setSearchTerm('')
    setResults([])
    setTarget(null)
    setReason('')
    setError(null)
    setSuccess(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MergeIcon />
          <Typography variant="h6">Merge Ticket {sourceTicketNumber}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Ticket merged successfully!</Alert>}

        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{sourceTicketNumber}</strong> will be closed and merged into the ticket you
          choose below. Its messages, attachments and watchers move to that ticket. This can't be undone.
        </Alert>

        {!target ? (
          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            <TextField
              label="Find the ticket to keep"
              placeholder="Search by number or subject…"
              fullWidth
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading || success}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searching ? <CircularProgress size={18} /> : <SearchIcon />}
                  </InputAdornment>
                )
              }}
              helperText="Type at least 2 characters"
            />
            {results.length > 0 && (
              <List dense sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {results.map((t) => (
                  <ListItemButton key={t.ticketId} onClick={() => { setTarget(t); setResults([]) }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">{t.ticketNumber}</Typography>
                          <Chip label={t.status} size="small" />
                        </Box>
                      }
                      secondary={t.subject}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            {!searching && searchTerm.trim().length >= 2 && results.length === 0 && (
              <Typography variant="caption" color="textSecondary">No matching tickets found.</Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary">Merging into (kept open):</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body1" fontWeight="medium">{target.ticketNumber}</Typography>
                <Chip label={target.status} size="small" />
                <Button size="small" onClick={() => setTarget(null)} disabled={loading || success}>Change</Button>
              </Box>
              <Typography variant="body2" color="textSecondary" noWrap>{target.subject}</Typography>
            </Box>

            <TextField
              label="Reason *"
              multiline
              rows={3}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading || success}
              inputProps={{ maxLength: 1000 }}
              helperText={`${reason.length}/1000 — recorded on both tickets (e.g. "Duplicate of ${target.ticketNumber}").`}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading || success || !target || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <MergeIcon />}
        >
          {loading ? 'Merging…' : 'Merge Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
